import { FC } from 'react';
import { Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import {
  BsFileEarmark,
  BsFileEarmarkZip,
  BsFiletypeCsv,
  BsFiletypePdf,
  BsFiletypeTxt,
  BsFiletypeXlsx,
} from 'react-icons/bs';

import { Link } from 'react-router-dom';

import {
  Directory,
  File,
  FileBrowserStorageObjectFragment,
} from '../../graphql/generated';
import { isFile } from '../../graphql/utils/utils';
import { isDirectory } from '../../graphql/utils/utils';
import { navigateToDirectory } from '../../pages/file-browser-page/file-browser-page';
import { navigateToFileDetailsRoute } from '../../pages/file-details-page/file-details-page';

import { FolderIcon } from '../../ui/icons/folder';
import { DltStatus } from '../file/dlt-status/dlt-status';

type Props = {
  object: FileBrowserStorageObjectFragment;
  enableTooltip?: boolean;
  asLink?: boolean;
};

export const generateDirectoryLink = (directory: Directory): string =>
  navigateToDirectory({
    cedantId: directory.cedant,
    directoryId: directory.id,
  });

export const generateFileLink = (file: Pick<File, 'id' | 'cedant'>): string =>
  navigateToFileDetailsRoute({
    cedantId: file.cedant,
    fileId: file.id,
  });

const Label: FC<Props> = ({ enableTooltip, object }) => {
  if (enableTooltip) {
    return (
      <Tooltip
        placement="start-start"
        label={`${object.cedant}/${object.path}${object.name}`}
      >
        <Text id={object.id} isTruncated>
          {object.name}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text id={object.id} isTruncated>
      {object.name}
    </Text>
  );
};

export const getContentTypeIcon = (
  file: Pick<File, 'contentType'>
): IconType => {
  const contentTypes = [
    {
      contentType:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      icon: BsFiletypeXlsx,
    },
    { contentType: 'application/pdf', icon: BsFiletypePdf },
    { contentType: 'text/plain', icon: BsFiletypeTxt },
    { contentType: 'text/csv', icon: BsFiletypeCsv },
    { contentType: 'application/zip', icon: BsFileEarmarkZip },
  ];

  const contentType = contentTypes.find((a) =>
    file.contentType.includes(a.contentType)
  );

  return contentType ? contentType.icon : BsFileEarmark;
};

export const Name: FC<Props> = ({
  object,
  enableTooltip = false,
  asLink = true,
}) => {
  if (isDirectory(object)) {
    return (
      <Flex
        as={asLink ? Link : undefined}
        isTruncated
        gridGap="3"
        alignItems="center"
        data-file-type="directory-link"
        to={generateDirectoryLink(object)}
      >
        <FolderIcon />
        <Label object={object} enableTooltip={enableTooltip} />
      </Flex>
    );
  }

  if (isFile(object)) {
    return (
      <Flex
        as={asLink ? Link : undefined}
        isTruncated
        gridGap="3"
        alignItems="center"
        data-file-type="file-link"
        to={generateFileLink(object)}
      >
        <Icon
          as={getContentTypeIcon(object)}
          fontSize="2rem"
          color="gray.500"
        />
        <Label object={object} enableTooltip={enableTooltip} />
        <DltStatus object={object} />
      </Flex>
    );
  }

  return null;
};
